import { DateTime } from "luxon";
import axios from "@/inside/axios";
import Snapshot from "@/inside/models/Snapshot";
import Textbase from "@/inside/models/Textbase/Textbase";

/**
 * Applies `Object.freeze()` recursively on given `object`.
 * @param {Object} candidate
 * @return {Object}
 */
function freeze(candidate) {
    if (typeof candidate !== "object") {
        throw new TypeError(`Function freeze() expects parameter 1 to be an object, ${typeof candidate} given`);
    }

    Object.freeze(candidate);
    Object.getOwnPropertyNames(candidate).forEach((property) => {
        if (typeof candidate[property] === "object") {
            freeze(candidate[property]);
        }
    });

    return candidate;
}

export default {
    actions: {
        /**
         * Fetches given snapshot, only if given `snapshot` data has not yet been fetched.
         * @param {Object} context
         * @param {Snapshot} snapshot
         * @return {Promise<Snapshot>}
         */
        fetch(context, snapshot) {
            return new Promise((resolve, reject) => {
                if (snapshot.isFetched) {
                    resolve(snapshot);
                }

                axios.get(`/textbases/${snapshot.textbase.id}/snapshots/${snapshot.id}`, {
                    validateStatus: status => status === 200
                }).then((response) => {
                    snapshot.data = freeze(response.data.data);
                    resolve(snapshot);
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        /**
         * Initializes module state.
         * @param {Function} commit
         * @param {Array} rootGetters
         * @return {Promise}
         */
        init({commit, rootGetters}) {
            commit("reset");

            return new Promise((resolve, reject) => {
                Promise.all(rootGetters["permissions/allTextbases"].map((textbase) => {
                    return axios.get(`/textbases/${textbase.id}/snapshots`, {
                        validateStatus: status => status === 200
                    }).then((response) => {
                        response.data.forEach((snapshot_data) => {
                            const snapshot = new Snapshot();
                            snapshot.id = snapshot_data.id;
                            snapshot.textbase = textbase;
                            snapshot.date = DateTime.fromFormat(snapshot_data.date, "yyyy-MM-dd HH:mm:ss");
                            commit("set", snapshot);
                        });
                    }).catch((error) => {
                        reject(error);
                    });
                })).then(() => {
                    resolve();
                });
            });
        }
    },
    getters: {
        /**
         * Searches for all snapshots corresponding to given `datetime` and returns them as an array of `Snapshot`
         * instances.
         * @param {Object} state
         * @return {function(DateTime): Snapshot[]}
         */
        findByDate(state) {
            return (date) => {
                if (!(date instanceof DateTime)) {
                    throw new TypeError(`Function returned by VueX 'dashboard/findByDate' getter expects parameter 1 to be an instance of DateTime, ${typeof date} given`);
                }

                return state.snapshots.filter((snapshot) => {
                    return (snapshot.date.toFormat("yyyyMMdd") === date.toFormat("yyyyMMdd"));
                });
            };
        },
        /**
         * Searches for all snapshots for given site ID. Returns found snapshots as an Array sorted by date.
         * @deprecated
         * @param {Object} state
         * @return {function(string): []}
         */
        findBySite(state) {
            return (siteID) => {
                return state.snapshots.filter((snapshot) => {
                    return (snapshot.textbase.id === siteID);
                }).sort((snapshotA, snapshotB) => {
                    return snapshotA.date - snapshotB.date;
                });
            };
        },
        /**
         * Searches for a snapshot using given siteID and given datetime object. Returns found snapshot or NULL if no
         * snapshot was found.
         * @deprecated
         * @param {Object} state
         * @return {function(string, datetime): Array|undefined}
         */
        findBySiteAndDate(state) {
            return (siteID, datetime) => {
                const exact = state.snapshots.find((snapshot) => {
                    return (
                        snapshot.textbase.id === siteID
                        && snapshot.date.toFormat("yyyyMMdd") === datetime.toFormat("yyyyMMdd")
                    );
                });

                if (exact instanceof Snapshot) {
                    return exact;
                }

                const result = state.snapshots.reduce((accumulator, snapshot) => {
                    if (snapshot.textbase.id !== siteID) {
                        return accumulator;
                    }

                    accumulator.textbaseSnapshots.push(snapshot);
                    accumulator.dates.push(snapshot.date);

                    return accumulator;
                }, {
                    dates: [],
                    textbaseSnapshots: []
                });

                let closestAnteriorDate = null;
                result.dates.forEach((date, index, array) => {
                    if (closestAnteriorDate === null && datetime <= date) {
                        closestAnteriorDate = array[index - 1];
                    }
                });

                return result.textbaseSnapshots.find((snapshot) => {
                    return snapshot.date.toFormat("yyyyMMdd") === closestAnteriorDate.toFormat("yyyyMMdd");
                });
            };
        },
        /**
         * Searches for all snapshots corresponding to given `textbase` and returns them as an array of `Snapshot`
         * instances.
         * @param {Object} state
         * @return {function(Textbase): Snapshot[]}
         */
        findByTextbase(state) {
            return (textbase) => {
                if (!(textbase instanceof Textbase)) {
                    throw new TypeError(`Function returned by VueX 'dashboard/findByTextbase' getter expects parameter 1 to be an instance of Textbase, ${typeof textbase} given`);
                }

                return state.snapshots.filter((snapshot) => {
                    return (snapshot.textbase.id === textbase.id);
                });
            }
        },
        /**
         * Returns any `Snapshot` instance corresponding to given `textbase` and given `date`, or `undefined` if no
         * corresponding snapshot was found.
         * @param {Object} state
         * @return {function(Textbase, DateTime): Snapshot|undefined}
         */
        findByTextbaseAndDate(state) {
            return (textbase, date) => {
                if (!(textbase instanceof Textbase)) {
                    throw new TypeError(`Function returned by VueX 'dashboard/findByTextbaseAndDate' getter expects parameter 1 to be an instance of Textbase, ${typeof textbase} given`);
                }

                if (!(date instanceof DateTime)) {
                    throw new TypeError(`Function returned by VueX 'dashboard/findByTextbaseAndDate' getter expects parameter 2 to be an instance of DateTime, ${typeof date} given`);
                }

                const exact = state.snapshots.find((snapshot) => {
                    return (
                        snapshot.textbase.id === textbase.id
                        && snapshot.date.toFormat("yyyyMMdd") === textbase.id.toFormat("yyyyMMdd")
                    );
                });

                if (exact instanceof Snapshot) {
                    return exact;
                }

                const result = state.snapshots.reduce((accumulator, snapshot) => {
                    if (snapshot.textbase.id !== textbase.id) {
                        return accumulator;
                    }

                    accumulator.textbaseSnapshots.push(snapshot);
                    accumulator.dates.push(snapshot.date);
                    return accumulator;
                }, {
                    dates: [],
                    textbaseSnapshots: []
                });

                let closestAnteriorDate = null;
                result.dates.forEach((availableDate, index, array) => {
                    if (closestAnteriorDate === null
                        && date < availableDate
                    ) {
                        closestAnteriorDate = array[index - 1];
                    }
                });

                return result.textbaseSnapshots.find((snapshot) => {
                    return snapshot.date.toFormat("yyyyMMdd") === closestAnteriorDate.toFormat("yyyyMMdd");
                });
            }
        }
    },
    mutations: {
        /**
         * Resets module state to empty.
         * @param {object} state
         */
        reset(state) {
            state.snapshots = [];
        },
        /**
         * Adds given `snapshot` to module state, replacing any snapshot corresponding to given `snapshot` text-base and
         * date currently stored in module state.
         * @param {Object} state
         * @param {Snapshot} snapshot
         */
        set(state, snapshot) {
            if (!(snapshot instanceof Snapshot)) {
                throw new TypeError(`VueX 'dashboard.set' mutation expects payload to be an instance of Snapshot, ${typeof snapshot} given`);
            }

            let found = false;
            state.snapshots = state.snapshots.map((existing_snapshot) => {
                if (
                    existing_snapshot.textbase.id === snapshot.textbase.id
                    && existing_snapshot.date.toFormat("yyyyMMdd") === snapshot.date.toFormat("yyyyMMdd")
                ) {
                    found = true;
                    return snapshot;
                } else {
                    return existing_snapshot;
                }
            });

            if (!found) {
                state.snapshots.push(snapshot);
            }
        }
    },
    namespaced: true,
    state: {
        snapshots: []
    }
}
